// Caminho absoluto: src/context/QuizContext.tsx

import React, { createContext, useReducer, useContext, ReactNode, useEffect, useRef } from 'react';
import type { QuizState, Pergunta, GameState, OverlayFeedbackState, Desafio } from '../types/quiz.types';
import type { ItemTendaKey } from '../types/quiz.types';
import type { SessaoPersistida } from '../types/progresso.types';

import { getCicloPorFase, getTimerPorPergunta, isActiveRunStatus, perderUltimoCheckpoint } from '../utils/quizUtils';
import { SoundManager } from '../utils/SoundManager';
import {
  extractPersistable,
  loadProgresso,
  mergeIntoInitial,
  needsSessionHydration,
  saveProgresso,
} from '../utils/progressStorage';
import { fetchPerguntasByFase } from '../services/quizFirebaseService';

const initialState: QuizState = {
  moedas: 0,
  vidas: 5,
  maxVidas: 5,
  ultimaVidaPerdida: null,
  inventario: { axe_dos_deuses: 0, ubuntu: 0, conselho_grios: 0, caminho_mandela: 0, escudo_palmares: 0 },
  faseAtual: 1,
  cicloAtual: 'I. Despertar',
  fasesConcluidas: [],
  tutorialConcluido: false,
  status: 'FASE_SELECT',
  perguntasFase: [],
  indicePerguntaAtual: 0,
  checkpointAtual: 0,
  pontuacaoFase: 0,
  comboAtual: 0,
  ajudasUsadasNestaFase: 0,
  tempoRestantePergunta: 0,
  usouAjudaNestaPergunta: false,
  desafioAtual: null,
  desafiosConcluidosNestaJornada: [],
  perguntaErro: null,
  conquistasDesbloqueadas: [],
  ultimoBonusDiario: null,
  dueloAtivo: false,
  dueloCheckpointAnterior: null,
  statusAntesDaTenda: null,
  tempoSalvoAntesDaTenda: null,
  feedbackMessage: null,
  feedbackType: 'info',
  overlayFeedback: { visible: false, context: null as OverlayFeedbackState['context'] },
  historicoMetricas: {},
};

function resetAttemptSession(): Pick<
  QuizState,
  | 'indicePerguntaAtual'
  | 'checkpointAtual'
  | 'vidas'
  | 'pontuacaoFase'
  | 'comboAtual'
  | 'ajudasUsadasNestaFase'
  | 'desafiosConcluidosNestaJornada'
  | 'dueloAtivo'
  | 'dueloCheckpointAnterior'
  | 'perguntaErro'
  | 'desafioAtual'
  | 'usouAjudaNestaPergunta'
  | 'statusAntesDaTenda'
  | 'tempoSalvoAntesDaTenda'
> {
  return {
    indicePerguntaAtual: 0,
    checkpointAtual: 0,
    vidas: 5,
    pontuacaoFase: 0,
    comboAtual: 0,
    ajudasUsadasNestaFase: 0,
    desafiosConcluidosNestaJornada: [],
    dueloAtivo: false,
    dueloCheckpointAnterior: null,
    perguntaErro: null,
    desafioAtual: null,
    usouAjudaNestaPergunta: false,
    statusAntesDaTenda: null,
    tempoSalvoAntesDaTenda: null,
  };
}

function clearSessionFields(): Pick<
  QuizState,
  | 'perguntasFase'
  | 'indicePerguntaAtual'
  | 'checkpointAtual'
  | 'pontuacaoFase'
  | 'comboAtual'
  | 'ajudasUsadasNestaFase'
  | 'desafiosConcluidosNestaJornada'
  | 'perguntaErro'
  | 'desafioAtual'
  | 'dueloAtivo'
  | 'dueloCheckpointAnterior'
  | 'statusAntesDaTenda'
  | 'tempoSalvoAntesDaTenda'
  | 'tempoRestantePergunta'
> {
  return {
    perguntasFase: [],
    indicePerguntaAtual: 0,
    checkpointAtual: 0,
    pontuacaoFase: 0,
    comboAtual: 0,
    ajudasUsadasNestaFase: 0,
    desafiosConcluidosNestaJornada: [],
    perguntaErro: null,
    desafioAtual: null,
    dueloAtivo: false,
    dueloCheckpointAnterior: null,
    statusAntesDaTenda: null,
    tempoSalvoAntesDaTenda: null,
    tempoRestantePergunta: 0,
  };
}

function addVidasComCap(vidas: number, quantidade: number, maxVidas: number): number {
  return Math.min(maxVidas, vidas + quantidade);
}

export type QuizAction =
  | { type: 'START_PHASE'; payload: { perguntas: Pergunta[]; fase: number } }
  | { type: 'RESTART_PHASE' }
  | { type: 'RESUME_FROM_CHECKPOINT' }
  | { type: 'HYDRATE_SESSION'; payload: { perguntas: Pergunta[]; sessao: SessaoPersistida } }
  | { type: 'ANSWER_CORRECT'; payload: { velocidadeBonus: number; desafioSorteado?: Desafio | null } }
  | { type: 'ANSWER_INCORRECT'; payload: Pergunta }
  | { type: 'CLOSE_ERROR_PORTAL' }
  | { type: 'COMPLETE_MINIGAME'; payload: { sucesso: boolean } }
  | { type: 'NAVIGATE'; payload: GameState }
  | { type: 'RETURN_FROM_TENDA' }
  | { type: 'FINISH_TUTORIAL' }
  | { type: 'COMPRAR_ITEM'; payload: { key: ItemTendaKey; custo: number; faseMinima?: number } }
  | { type: 'COMPRAR_VIDAS'; payload: { quantidade: number; custo: number } }
  | { type: 'USAR_AJUDA'; payload: { tipo: keyof QuizState['inventario']; pergunta?: Pergunta } }
  | { type: 'PULAR_PERGUNTA'; payload?: { desafioSorteado?: Desafio | null } }
  | { type: 'SET_TIMER'; payload: number }
  | { type: 'TICK_TIMER' }
  | { type: 'CLAIM_BONUS_DIARIO'; payload: number }
  | { type: 'SHOW_FEEDBACK'; payload: { message: string; type?: 'success' | 'info' | 'warning' | 'error' | 'duelo' } }
  | { type: 'CLEAR_FEEDBACK' }
  | { type: 'SHOW_OVERLAY_FEEDBACK'; payload: { context: OverlayFeedbackState['context']; result: 'success' | 'failure'; message: string } }
  | { type: 'HIDE_OVERLAY_FEEDBACK' };

function quizReducer(state: QuizState, action: QuizAction): QuizState {
  switch (action.type) {
    case 'NAVIGATE': {
      if (action.payload === 'TENDA' && isActiveRunStatus(state.status)) {
        return {
          ...state,
          status: 'TENDA',
          statusAntesDaTenda: state.status,
          tempoSalvoAntesDaTenda: state.tempoRestantePergunta,
        };
      }

      if (action.payload === 'MENU' || action.payload === 'FASE_SELECT') {
        return {
          ...state,
          ...clearSessionFields(),
          status: action.payload,
          vidas: 5,
        };
      }

      return { ...state, status: action.payload };
    }

    case 'RETURN_FROM_TENDA': {
      if (!state.statusAntesDaTenda) {
        return { ...state, status: 'FASE_SELECT' };
      }
      const timer =
        state.tempoSalvoAntesDaTenda ??
        (state.statusAntesDaTenda === 'PLAYING' ? getTimerPorPergunta(state.faseAtual) : 0);
      return {
        ...state,
        status: state.statusAntesDaTenda,
        tempoRestantePergunta: timer,
        statusAntesDaTenda: null,
        tempoSalvoAntesDaTenda: null,
      };
    }

    case 'FINISH_TUTORIAL':
      return { ...state, tutorialConcluido: true, status: 'FASE_SELECT' };

    case 'HYDRATE_SESSION': {
      const { perguntas, sessao } = action.payload;
      const safeIndex = (sessao.indicePerguntaAtual >= 0 && sessao.indicePerguntaAtual < perguntas.length)
        ? sessao.indicePerguntaAtual
        : 0;
      const restoredStatus = (sessao.status === 'ERROR_PORTAL' || sessao.status === 'MINIGAME')
        ? 'PLAYING'
        : sessao.status;

      return {
        ...state,
        perguntasFase: perguntas,
        faseAtual: sessao.faseAtual,
        cicloAtual: getCicloPorFase(sessao.faseAtual),
        status: restoredStatus,
        indicePerguntaAtual: safeIndex,
        checkpointAtual: sessao.checkpointAtual,
        vidas: sessao.vidas > 0 ? sessao.vidas : 5,
        comboAtual: sessao.comboAtual,
        pontuacaoFase: sessao.pontuacaoFase,
        ajudasUsadasNestaFase: sessao.ajudasUsadasNestaFase,
        desafiosConcluidosNestaJornada: [...sessao.desafiosConcluidosNestaJornada],
        tempoRestantePergunta: sessao.tempoRestantePergunta > 0 ? sessao.tempoRestantePergunta : getTimerPorPergunta(sessao.faseAtual),
        dueloAtivo: sessao.dueloAtivo,
      };
    }

    case 'START_PHASE': {
      const { perguntas, fase } = action.payload;
      const cicloAtual = getCicloPorFase(fase);

      const stats = state.historicoMetricas[fase] || {
        jogadas: 0, vitorias: 0, derrotas: 0, acertos: 0, erros: 0, tempoTotalGasto: 0, maiorCombo: 0, historicoPerguntas: {}
      };

      return {
        ...state,
        ...resetAttemptSession(),
        status: 'PLAYING',
        faseAtual: fase,
        cicloAtual,
        perguntasFase: perguntas,
        historicoMetricas: {
          ...state.historicoMetricas,
          [fase]: { ...stats, jogadas: stats.jogadas + 1 }
        },
        tempoRestantePergunta: getTimerPorPergunta(fase),
        feedbackMessage: `Nova tentativa: 5 Búzios Vitais. Ciclo ${cicloAtual}.`,
        feedbackType: 'info',
      };
    }

    case 'RESTART_PHASE': {
      if (state.perguntasFase.length === 0) return state;
      return {
        ...state,
        ...resetAttemptSession(),
        status: 'PLAYING',
        tempoRestantePergunta: getTimerPorPergunta(state.faseAtual),
        feedbackMessage: 'Fase reiniciada da pergunta 1 com 5 Búzios Vitais.',
        feedbackType: 'info',
      };
    }

    case 'RESUME_FROM_CHECKPOINT': {
      if (state.checkpointAtual <= 0) return state;
      return {
        ...state,
        status: 'PLAYING',
        indicePerguntaAtual: state.checkpointAtual,
        vidas: 2,
        comboAtual: 0,
        perguntaErro: null,
        tempoRestantePergunta: getTimerPorPergunta(state.faseAtual),
        feedbackMessage: 'Última chance: save point retomado com 2 Búzios Vitais.',
        feedbackType: 'warning',
      };
    }

    case 'ANSWER_CORRECT': {
      const isLastQuestion = state.indicePerguntaAtual === 15;
      const nextIndex = state.indicePerguntaAtual + 1;

      let novoCheckpoint = state.checkpointAtual;
      if (state.indicePerguntaAtual === 7 || state.indicePerguntaAtual === 13) {
        novoCheckpoint = nextIndex;
      }

      let nextStatus: GameState = 'PLAYING';

      const baseGanho = 10 + action.payload.velocidadeBonus + (state.comboAtual >= 4 ? 20 : 0);
      let moedasGanhas = baseGanho;
      let feedbackMessage = state.feedbackMessage;
      let feedbackType = state.feedbackType;

      if (state.dueloAtivo && !state.usouAjudaNestaPergunta) {
        moedasGanhas *= 2;
        SoundManager.playDueloVitoria();
        feedbackMessage = 'Duelo vencido! O Axé desta pergunta foi dobrado.';
        feedbackType = 'duelo';
      }

      if (!state.dueloAtivo) {
        feedbackMessage = `Resposta correta! +${moedasGanhas} Axé.`;
        feedbackType = 'success';
      }

      if (state.indicePerguntaAtual === 7 || state.indicePerguntaAtual === 13) {
        feedbackMessage = `Save point registrado (pergunta ${nextIndex + 1}). Disponível no game over.`;
        feedbackType = 'info';
      }

      const currentQ = state.perguntasFase[state.indicePerguntaAtual];
      const maxTimer = getTimerPorPergunta(state.faseAtual);
      const tempoDesperdicado = maxTimer - state.tempoRestantePergunta;

      const stats = state.historicoMetricas[state.faseAtual];
      const newCombo = state.comboAtual + 1;
      const metricsUpdated = {
        ...stats,
        acertos: stats.acertos + 1,
        tempoTotalGasto: stats.tempoTotalGasto + tempoDesperdicado,
        maiorCombo: Math.max(stats.maiorCombo, newCombo),
        historicoPerguntas: {
          ...stats.historicoPerguntas,
          [currentQ.id]: { id: currentQ.id, texto: currentQ.pergunta, status: 'acerto' as const }
        }
      };

      if (isLastQuestion) {
        const novasFasesConcluidas = state.fasesConcluidas.includes(state.faseAtual)
          ? state.fasesConcluidas
          : [...state.fasesConcluidas, state.faseAtual];
        const bonusMestre = state.ajudasUsadasNestaFase === 0 ? 200 : 0;
        const ganhoTotal = moedasGanhas + bonusMestre;
        nextStatus = state.faseAtual === 50 ? 'VICTORY_FASE_50' : 'VICTORY';
        return {
          ...state,
          moedas: state.moedas + ganhoTotal,
          comboAtual: newCombo,
          pontuacaoFase: state.pontuacaoFase + ganhoTotal,
          indicePerguntaAtual: nextIndex,
          checkpointAtual: novoCheckpoint,
          status: nextStatus,
          fasesConcluidas: novasFasesConcluidas,
          historicoMetricas: {
            ...state.historicoMetricas,
            [state.faseAtual]: { ...metricsUpdated, vitorias: metricsUpdated.vitorias + 1 }
          },
          tempoRestantePergunta: 0,
          dueloAtivo: false,
          dueloCheckpointAnterior: null,
          feedbackMessage,
          feedbackType,
        };
      }

      if (state.indicePerguntaAtual === 3 || state.indicePerguntaAtual === 11) {
        nextStatus = 'MINIGAME';
      }

      const timerProxima = nextStatus === 'MINIGAME' ? 0 : getTimerPorPergunta(state.faseAtual);

      return {
        ...state,
        moedas: state.moedas + moedasGanhas,
        comboAtual: newCombo,
        pontuacaoFase: state.pontuacaoFase + moedasGanhas,
        indicePerguntaAtual: nextIndex,
        checkpointAtual: novoCheckpoint,
        historicoMetricas: {
          ...state.historicoMetricas,
          [state.faseAtual]: metricsUpdated
        },
        status: nextStatus,
        desafioAtual:
          nextStatus === 'MINIGAME'
            ? action.payload.desafioSorteado ?? null
            : null,
        usouAjudaNestaPergunta: false,
        tempoRestantePergunta: timerProxima,
        dueloAtivo: false,
        dueloCheckpointAnterior: null,
        feedbackMessage,
        feedbackType,
      };
    }

    case 'ANSWER_INCORRECT': {
      let novasVidas = state.vidas;
      const novoInventario = { ...state.inventario };
      let novoCheckpointAtual = state.checkpointAtual;
      let feedbackMessage = state.feedbackMessage;
      let feedbackType = state.feedbackType;

      if (state.dueloAtivo) {
        novoCheckpointAtual = perderUltimoCheckpoint(state.checkpointAtual);
        SoundManager.playDueloDerrota();
        feedbackMessage = 'Duelo perdido! Você perdeu o último save point.';
        feedbackType = 'warning';
      } else {
        feedbackMessage = 'Resposta incorreta. Você perdeu 1 Búzio Vital.';
        feedbackType = 'error';
      }

      if (state.inventario.escudo_palmares > 0) {
        novoInventario.escudo_palmares -= 1;
        if (!state.dueloAtivo) {
          feedbackMessage = 'Escudo de Palmares absorveu o erro! Búzios Vitais preservados.';
          feedbackType = 'info';
        }
      } else {
        novasVidas = Math.max(0, state.vidas - 1);
      }

      const currentQ = action.payload;
      const maxTimer = getTimerPorPergunta(state.faseAtual);
      const tempoDesperdicado = maxTimer - state.tempoRestantePergunta;
      const stats = state.historicoMetricas[state.faseAtual];

      const metricsUpdated = {
        ...stats,
        erros: stats.erros + 1,
        tempoTotalGasto: stats.tempoTotalGasto + tempoDesperdicado,
        historicoPerguntas: {
          ...stats.historicoPerguntas,
          [currentQ.id]: { id: currentQ.id, texto: currentQ.pergunta, status: 'erro' as const }
        }
      };

      if (novasVidas === 0) {
        return {
          ...state,
          status: 'GAME_OVER',
          vidas: 0,
          inventario: novoInventario,
          checkpointAtual: novoCheckpointAtual,
          historicoMetricas: {
            ...state.historicoMetricas,
            [state.faseAtual]: { ...metricsUpdated, derrotas: metricsUpdated.derrotas + 1 }
          },
          tempoRestantePergunta: 0,
          dueloAtivo: false,
          dueloCheckpointAnterior: null,
          perguntaErro: action.payload,
          feedbackMessage,
          feedbackType,
        };
      }

      return {
        ...state,
        vidas: novasVidas,
        comboAtual: 0,
        inventario: novoInventario,
        status: 'ERROR_PORTAL',
        historicoMetricas: {
          ...state.historicoMetricas,
          [state.faseAtual]: metricsUpdated
        },
        perguntaErro: action.payload,
        tempoRestantePergunta: 0,
        checkpointAtual: novoCheckpointAtual,
        dueloAtivo: false,
        dueloCheckpointAnterior: null,
        feedbackMessage,
        feedbackType,
      };
    }

    case 'CLOSE_ERROR_PORTAL':
      return {
        ...state,
        status: 'PLAYING',
        perguntaErro: null,
        tempoRestantePergunta: getTimerPorPergunta(state.faseAtual),
      };

    case 'COMPLETE_MINIGAME': {
      const desafio = state.desafioAtual;
      const idsConcluidos = desafio
        ? [...state.desafiosConcluidosNestaJornada, desafio.id]
        : state.desafiosConcluidosNestaJornada;

      if (desafio && desafio.tipo === 'DUELO_ANCESTRAIS') {
        if (action.payload.sucesso) {
          SoundManager.playDueloAceito();
          return {
            ...state,
            status: 'PLAYING',
            desafioAtual: null,
            desafiosConcluidosNestaJornada: idsConcluidos,
            dueloAtivo: true,
            dueloCheckpointAnterior: state.checkpointAtual,
            tempoRestantePergunta: getTimerPorPergunta(state.faseAtual),
            feedbackMessage: 'Aposta aceita! O Axé será dobrado.',
            feedbackType: 'duelo',
          };
        }
        SoundManager.playClick();
        return {
          ...state,
          status: 'PLAYING',
          desafioAtual: null,
          desafiosConcluidosNestaJornada: idsConcluidos,
          dueloAtivo: false,
          dueloCheckpointAnterior: null,
          tempoRestantePergunta: getTimerPorPergunta(state.faseAtual),
          feedbackMessage: 'Duelo recusado.',
          feedbackType: 'info',
        };
      }

      const moedasExtra =
        action.payload.sucesso && desafio ? desafio.recompensa.moedas : 0;
      return {
        ...state,
        status: 'PLAYING',
        desafioAtual: null,
        moedas: state.moedas + moedasExtra,
        desafiosConcluidosNestaJornada: idsConcluidos,
        tempoRestantePergunta: getTimerPorPergunta(state.faseAtual),
        dueloAtivo: false,
        dueloCheckpointAnterior: null,
        feedbackMessage:
          action.payload.sucesso && desafio
            ? `Desafio concluído! +${moedasExtra} Axé.`
            : !action.payload.sucesso && desafio
              ? 'Desafio não concluído.'
              : state.feedbackMessage,
        feedbackType:
          action.payload.sucesso && desafio
            ? 'success'
            : !action.payload.sucesso && desafio
              ? 'warning'
              : state.feedbackType,
      };
    }

    case 'COMPRAR_ITEM': {
      const { key, custo, faseMinima } = action.payload;
      if (faseMinima && state.faseAtual < faseMinima) {
        return {
          ...state,
          feedbackMessage: `Disponível a partir da Fase ${faseMinima}.`,
          feedbackType: 'warning',
        };
      }
      if (state.moedas < custo) {
        return {
          ...state,
          feedbackMessage: 'Axé insuficiente para esta compra.',
          feedbackType: 'warning',
        };
      }
      const novaMoedas = state.moedas - custo;

      if (key === 'vida_1') {
        if (state.vidas >= state.maxVidas) {
          return {
            ...state,
            feedbackMessage: 'Sua barra de Búzios Vitais já está cheia (5/5).',
            feedbackType: 'info',
          };
        }
        const v = addVidasComCap(state.vidas, 1, state.maxVidas);
        return {
          ...state,
          moedas: novaMoedas,
          vidas: v,
          feedbackMessage: '+1 Búzio Vital adicionado.',
          feedbackType: 'success',
        };
      }

      if (key === 'vidas_4' || key === 'vidas_5') {
        if (state.vidas >= state.maxVidas) {
          return {
            ...state,
            feedbackMessage: 'Sua barra de Búzios Vitais já está cheia (5/5).',
            feedbackType: 'info',
          };
        }
        const quantidade = 4;
        const espaco = state.maxVidas - state.vidas;
        if (espaco < quantidade && espaco <= 0) {
          return {
            ...state,
            feedbackMessage: 'Sua barra de Búzios Vitais já está cheia (5/5).',
            feedbackType: 'info',
          };
        }
        const v = addVidasComCap(state.vidas, quantidade, state.maxVidas);
        const adicionados = v - state.vidas;
        return {
          ...state,
          moedas: novaMoedas,
          vidas: v,
          feedbackMessage:
            adicionados < quantidade
              ? `+${adicionados} Búzios adicionados (máximo atingido).`
              : '+4 Búzios Vitais adicionados.',
          feedbackType: 'success',
        };
      }

      const inv = { ...state.inventario };
      if (key in inv) {
        (inv as Record<string, number>)[key] = (inv[key as keyof typeof inv] as number) + 1;
        const nomeItem = key === 'axe_dos_deuses'
          ? 'Axé dos Deuses'
          : key === 'ubuntu'
            ? 'Ubuntu'
            : key === 'conselho_grios'
              ? 'Conselho dos Grios'
              : key === 'caminho_mandela'
                ? 'Caminho de Mandela'
                : key === 'escudo_palmares'
                  ? 'Escudo de Palmares'
                  : 'Item';
        return {
          ...state,
          moedas: novaMoedas,
          inventario: inv,
          feedbackMessage: `Você comprou 1x ${nomeItem} por ${custo} Axé.`,
          feedbackType: 'success',
        };
      }
      return state;
    }

    case 'COMPRAR_VIDAS': {
      const { quantidade, custo } = action.payload;
      if (state.moedas < custo) {
        return {
          ...state,
          feedbackMessage: 'Axé insuficiente para esta compra.',
          feedbackType: 'warning',
        };
      }
      if (state.vidas >= state.maxVidas) {
        return {
          ...state,
          feedbackMessage: 'Sua barra de Búzios Vitais já está cheia (5/5).',
          feedbackType: 'info',
        };
      }
      const v = addVidasComCap(state.vidas, quantidade, state.maxVidas);
      const adicionados = v - state.vidas;
      return {
        ...state,
        moedas: state.moedas - custo,
        vidas: v,
        feedbackMessage:
          adicionados < quantidade
            ? `+${adicionados} Búzios adicionados (máximo atingido).`
            : quantidade === 1
              ? '+1 Búzio Vital adicionado.'
              : '+4 Búzios Vitais adicionados.',
        feedbackType: 'success',
      };
    }

    case 'USAR_AJUDA': {
      const { tipo } = action.payload;
      const inv = { ...state.inventario };
      const qty = inv[tipo];
      if (qty <= 0) return state;
      inv[tipo] = qty - 1;
      return {
        ...state,
        inventario: inv,
        ajudasUsadasNestaFase: state.ajudasUsadasNestaFase + 1,
        usouAjudaNestaPergunta: true,
      };
    }

    case 'PULAR_PERGUNTA': {
      const inv = { ...state.inventario };
      if (inv.caminho_mandela <= 0) return state;
      inv.caminho_mandela -= 1;
      const nextIdx = state.indicePerguntaAtual + 1;
      const isLast = nextIdx > 15;
      let nextStatus: GameState = 'PLAYING';
      if (!isLast && (state.indicePerguntaAtual === 3 || state.indicePerguntaAtual === 11)) {
        nextStatus = 'MINIGAME';
      } else if (isLast) {
        nextStatus = state.faseAtual === 50 ? 'VICTORY_FASE_50' : 'VICTORY';
        const novasFases = state.fasesConcluidas.includes(state.faseAtual) ? state.fasesConcluidas : [...state.fasesConcluidas, state.faseAtual];
        return { ...state, inventario: inv, ajudasUsadasNestaFase: state.ajudasUsadasNestaFase + 1, indicePerguntaAtual: nextIdx, status: nextStatus, fasesConcluidas: novasFases, desafioAtual: null, tempoRestantePergunta: 0 };
      }
      return {
        ...state,
        inventario: inv,
        ajudasUsadasNestaFase: state.ajudasUsadasNestaFase + 1,
        indicePerguntaAtual: nextIdx,
        status: nextStatus,
        desafioAtual:
          nextStatus === 'MINIGAME'
            ? action.payload?.desafioSorteado ?? null
            : null,
        tempoRestantePergunta: getTimerPorPergunta(state.faseAtual),
      };
    }

    case 'SET_TIMER':
      return { ...state, tempoRestantePergunta: action.payload };

    case 'TICK_TIMER':
      if (state.tempoRestantePergunta <= 0) return state;
      return { ...state, tempoRestantePergunta: state.tempoRestantePergunta - 1 };

    case 'CLAIM_BONUS_DIARIO':
      return { ...state, moedas: state.moedas + action.payload, ultimoBonusDiario: Date.now() };

    case 'SHOW_FEEDBACK':
      return {
        ...state,
        feedbackMessage: action.payload.message,
        feedbackType: action.payload.type ?? 'info',
      };

    case 'CLEAR_FEEDBACK':
      return {
        ...state,
        feedbackMessage: null,
      };

    case 'SHOW_OVERLAY_FEEDBACK':
      return {
        ...state,
        overlayFeedback: {
          visible: true,
          context: action.payload.context,
          result: action.payload.result,
          message: action.payload.message,
        },
        tempoRestantePergunta: 0,
      };

    case 'HIDE_OVERLAY_FEEDBACK': {
      const shouldRestartTimer =
        state.status === 'PLAYING' && state.perguntasFase.length > 0 && state.indicePerguntaAtual < state.perguntasFase.length;
      return {
        ...state,
        overlayFeedback: { visible: false, context: null },
        tempoRestantePergunta:
          shouldRestartTimer && state.tempoRestantePergunta === 0
            ? getTimerPorPergunta(state.faseAtual)
            : state.tempoRestantePergunta,
      };
    }

    default:
      return state;
  }
}

const QuizContext = createContext<{
  state: QuizState;
  dispatch: React.Dispatch<QuizAction>;
} | null>(null);

function createInitialState(): QuizState {
  return mergeIntoInitial(loadProgresso(), initialState);
}

export const QuizProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(quizReducer, undefined, createInitialState);
  const isFirstRender = useRef(true);
  const sessionHydrated = useRef(false);

  useEffect(() => {
    if (sessionHydrated.current) return;
    const persisted = loadProgresso();
    if (!needsSessionHydration(persisted) || !persisted?.sessao) {
      sessionHydrated.current = true;
      return;
    }

    fetchPerguntasByFase(persisted.sessao.faseAtual)
      .then((perguntas) => {
        if (!perguntas || perguntas.length === 0) {
          dispatch({ type: 'NAVIGATE', payload: 'MENU' });
        } else {
          dispatch({ type: 'HYDRATE_SESSION', payload: { perguntas, sessao: persisted.sessao! } });
        }
        sessionHydrated.current = true;
      })
      .catch(() => {
        dispatch({ type: 'NAVIGATE', payload: 'MENU' });
        sessionHydrated.current = true;
      });
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const timeoutId = window.setTimeout(() => {
      saveProgresso(extractPersistable(state));
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [
    state.moedas,
    state.vidas,
    state.ultimaVidaPerdida,
    state.inventario,
    state.faseAtual,
    state.fasesConcluidas,
    state.tutorialConcluido,
    state.conquistasDesbloqueadas,
    state.ultimoBonusDiario,
    state.historicoMetricas,
    state.status,
    state.indicePerguntaAtual,
    state.checkpointAtual,
    state.comboAtual,
    state.pontuacaoFase,
    state.ajudasUsadasNestaFase,
    state.desafiosConcluidosNestaJornada,
    state.tempoRestantePergunta,
    state.dueloAtivo,
  ]);

  return (
    <QuizContext.Provider value={{ state, dispatch }}>
      {children}
    </QuizContext.Provider>
  );
};

export const useQuiz = () => {
  const context = useContext(QuizContext);
  if (!context) throw new Error('useQuiz deve ser usado dentro de um QuizProvider');
  return context;
};
